import type { ShopifyProduct } from "@/lib/shopify";
import type { Locale } from "@/stores/i18nStore";

export type CollectionSlug =
  | "new"
  | "dynasty-koi"
  | "carbon"
  | "floral-art"
  | "marble-luxe"
  | "leather-matte";

type LocalizedText = Record<Locale, string>;

export type MerchCollection = {
  slug: CollectionSlug;
  label: LocalizedText;
  description: LocalizedText;
  keywords: string[];
};

export const COLLECTIONS: MerchCollection[] = [
  {
    slug: "new",
    label: { fr: "Nouveautés", en: "New arrivals", nl: "Nieuw", pl: "Nowości", ro: "Noutăți" },
    description: {
      fr: "Les derniers designs ajoutés au Vault.",
      en: "The latest designs added to the Vault.",
      nl: "De nieuwste designs in de Vault.",
      pl: "Najnowsze wzory dodane do Vault.",
      ro: "Cele mai noi designuri din Vault.",
    },
    keywords: [],
  },
  {
    slug: "dynasty-koi",
    label: { fr: "Dynasty Koi", en: "Dynasty Koi", nl: "Dynasty Koi", pl: "Dynasty Koi", ro: "Dynasty Koi" },
    description: {
      fr: "Notre collection japonaise signature, disponible sur plusieurs appareils.",
      en: "Our signature Japanese collection, available across devices.",
      nl: "Onze Japanse signature-collectie voor meerdere apparaten.",
      pl: "Nasza japońska kolekcja signature na wiele urządzeń.",
      ro: "Colecția noastră japoneză signature, pentru mai multe dispozitive.",
    },
    keywords: ["dynasty koi", "dynasty-koi", "koi"],
  },
  {
    slug: "carbon",
    label: { fr: "Carbone", en: "Carbon", nl: "Carbon", pl: "Carbon", ro: "Carbon" },
    description: {
      fr: "Textures techniques et look performance.",
      en: "Technical textures with a performance look.",
      nl: "Technische texturen met een performance-look.",
      pl: "Techniczne tekstury i sportowy wygląd.",
      ro: "Texturi tehnice cu aspect performance.",
    },
    keywords: ["carbon", "carbone"],
  },
  {
    slug: "floral-art",
    label: { fr: "Floral & artistique", en: "Floral & artistic", nl: "Floraal & artistiek", pl: "Kwiatowe & artystyczne", ro: "Floral & artistic" },
    description: {
      fr: "Motifs expressifs, floraux et illustrés.",
      en: "Expressive floral and illustrated designs.",
      nl: "Expressieve bloemen- en illustratiedesigns.",
      pl: "Wyraziste wzory kwiatowe i ilustracyjne.",
      ro: "Designuri florale și ilustrate, pline de expresie.",
    },
    keywords: ["floral", "flower", "fleur", "autumn", "botanical", "artist", "artistic"],
  },
  {
    slug: "marble-luxe",
    label: { fr: "Marbre & luxe", en: "Marble & luxury", nl: "Marmer & luxe", pl: "Marmur & luksus", ro: "Marmură & lux" },
    description: {
      fr: "Finitions raffinées, marbrées et métalliques.",
      en: "Refined marble and metallic finishes.",
      nl: "Verfijnde marmer- en metallicafwerkingen.",
      pl: "Wyrafinowane marmurowe i metaliczne wykończenia.",
      ro: "Finisaje rafinate, marmură și accente metalice.",
    },
    keywords: ["marble", "marbre", "gold", "golden", "luxury", "luxe", "metallic"],
  },
  {
    slug: "leather-matte",
    label: { fr: "Cuir & mat", en: "Leather & matte", nl: "Leer & mat", pl: "Skóra & mat", ro: "Piele & mat" },
    description: {
      fr: "Sobriété premium, toucher texturé et couleurs profondes.",
      en: "Premium restraint, tactile texture and deep colours.",
      nl: "Premium eenvoud, voelbare textuur en diepe kleuren.",
      pl: "Premium minimalizm, wyczuwalna tekstura i głębokie kolory.",
      ro: "Minimalism premium, textură tactilă și culori profunde.",
    },
    keywords: ["leather", "cuir", "matte", " matt", "black matte", "skin noir"],
  },
];

const normalizedProductText = (product: ShopifyProduct) => {
  const { node } = product;
  return [
    node.title,
    node.handle,
    node.description,
    node.productType,
    ...(node.tags || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

export function getCollection(slug?: string) {
  return COLLECTIONS.find((collection) => collection.slug === slug);
}

export function getCollectionCopy(collection: MerchCollection, locale: Locale) {
  return {
    label: collection.label[locale] || collection.label.en,
    description: collection.description[locale] || collection.description.en,
  };
}

export function matchesCollection(product: ShopifyProduct, slug: CollectionSlug) {
  const collection = getCollection(slug);
  if (!collection) return false;
  if (slug === "new") return true;
  const haystack = normalizedProductText(product);
  return collection.keywords.some((keyword) => haystack.includes(keyword));
}

export type ProductSort = "recommended" | "newest" | "price-asc" | "price-desc";

export function sortProducts(products: ShopifyProduct[], sort: ProductSort) {
  const list = [...products];
  if (sort === "newest") {
    return list.sort((a, b) => {
      const aDate = a.node.createdAt ? Date.parse(a.node.createdAt) : 0;
      const bDate = b.node.createdAt ? Date.parse(b.node.createdAt) : 0;
      return bDate - aDate;
    });
  }
  if (sort === "price-asc" || sort === "price-desc") {
    const direction = sort === "price-asc" ? 1 : -1;
    return list.sort(
      (a, b) =>
        (Number(a.node.priceRange.minVariantPrice.amount) -
          Number(b.node.priceRange.minVariantPrice.amount)) *
        direction,
    );
  }
  return list;
}

export type DeviceGroupSlug =
  | "phones"
  | "laptops"
  | "tablets"
  | "cameras"
  | "gaming"
  | "cards"
  | "accessories";

export function getDeviceGroup(product: ShopifyProduct): DeviceGroupSlug {
  const text = normalizedProductText(product);
  if (/credit card|card skin|bank card/.test(text)) return "cards";
  if (/screen protector|accessor|squeegee|application tool/.test(text)) return "accessories";
  if (/camera|canon|nikon|sony|lens/.test(text)) return "cameras";
  if (/console|controller|ps4|ps5|xbox|switch|nintendo|gaming/.test(text)) return "gaming";
  if (/tablet|ipad|galaxy tab|surface/.test(text)) return "tablets";
  if (/macbook|laptop|notebook|dell|lenovo|asus|thinkpad/.test(text)) return "laptops";
  return "phones";
}
